import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CalendarDays, DollarSign, Scissors, MapPin, Play, CheckCircle2, Truck, Sparkles, Route, Link2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_LABELS = {
  scheduled: { label: "Scheduled", color: "bg-blue-50 text-blue-700 border-blue-200" },
  en_route: { label: "En route", color: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress: { label: "Grooming", color: "bg-orange-50 text-orange-700 border-orange-200" },
  completed: { label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", color: "bg-rose-50 text-rose-700 border-rose-200" },
};
const NEXT = { scheduled: "en_route", en_route: "in_progress", in_progress: "completed" };
const NEXT_LABEL = { scheduled: "Start route", en_route: "Arrived – begin", in_progress: "Mark complete" };
const NEXT_ICON = { scheduled: Truck, en_route: Play, in_progress: CheckCircle2 };

function isToday(iso) {
  const d = new Date(iso); const t = new Date();
  return d.toDateString() === t.toDateString();
}

export default function Dashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const inviteLink = user?.slug ? `${window.location.origin}/join/${user.slug}` : "";
  const copyInvite = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Invite link copied — text it to your clients");
      setTimeout(() => setCopied(false), 2200);
    } catch { toast.error("Couldn't copy — long-press the link"); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/appointments");
      setAppts(data);
    } catch { toast.error("Failed to load appointments"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const advance = async (a) => {
    const next = NEXT[a.status]; if (!next) return;
    try {
      const { data } = await api.put(`/appointments/${a.id}/status`, { status: next });
      setAppts(prev => prev.map(x => x.id === a.id ? data : x));
      toast.success(`Marked ${STATUS_LABELS[next].label}`);
    } catch { toast.error("Failed to update status"); }
  };

  const optimize = async () => {
    try {
      await api.post("/appointments/route/optimize");
      await load();
      toast.success("Route optimized by ZIP + address");
    } catch { toast.error("Optimize failed"); }
  };

  const today = appts.filter(a => isToday(a.start))
    .sort((a, b) => (a.route_order ?? 0) - (b.route_order ?? 0) || new Date(a.start) - new Date(b.start));;
  const upcoming = appts.filter(a => new Date(a.start) > new Date() && !isToday(a.start)).slice(0, 5);
  const revenue = today.filter(a => a.status !== "cancelled").reduce((s, a) => s + (a.price || 0), 0);
  const completed = today.filter(a => a.status === "completed").length;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">Today · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Route command</h1>
        </div>
        <div className="pill bg-[#2D4A3E] text-white">
          <Sparkles className="w-3 h-3"/> {today.length} stops today
        </div>
      </header>

      {/* Invite link — share with existing client list */}
      {inviteLink && (
        <section className="rounded-3xl border border-[#E5DCCE] bg-white p-4 flex flex-wrap items-center gap-3"
          data-testid="invite-link-card">
          <div className="w-10 h-10 rounded-xl bg-[#F5E6CF] text-[#A9480F] grid place-content-center">
            <Link2 className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Your client invite link</div>
            <div className="font-mono text-sm text-[#1F1A17] truncate" data-testid="invite-link-value">{inviteLink}</div>
          </div>
          <Button size="sm" onClick={copyInvite}
            className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile"
            data-testid="btn-copy-invite">
            {copied ? <><Check className="w-3.5 h-3.5 mr-1"/> Copied</> : <>Copy link</>}
          </Button>
        </section>
      )}

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CalendarDays} label="Today's stops" value={today.length} tint="#F5E6CF" fg="#A9480F" tid="stat-today"/>
        <StatCard icon={CheckCircle2} label="Completed" value={completed} tint="#D8EFDF" fg="#166534" tid="stat-completed"/>
        <StatCard icon={DollarSign} label="Today's revenue" value={`$${revenue.toFixed(0)}`} tint="#FCE7C9" fg="#7A3A09" tid="stat-revenue"/>
        <StatCard icon={Scissors} label="Total booked" value={appts.filter(a=>a.status!=="cancelled").length} tint="#DDE7E3" fg="#21382E" tid="stat-total"/>
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Today's timeline</h2>
            <Button size="sm" onClick={optimize} variant="outline"
              className="rounded-full border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white"
              data-testid="btn-optimize-route">
              <Route className="w-3.5 h-3.5 mr-1"/> Optimize
            </Button>
          </div>
          {loading ? <p className="text-sm text-[#6B625B]">Loading…</p> :
            today.length === 0 ? (
              <div className="text-center py-10 text-[#6B625B]">
                <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40"/>
                <p className="text-sm">No stops today. Enjoy the quiet.</p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="today-list">
                {today.map(a => {
                  const s = STATUS_LABELS[a.status];
                  const Icon = NEXT_ICON[a.status];
                  return (
                    <li key={a.id} className="rounded-2xl border border-[#E5DCCE] p-4 flex items-center gap-4 card-tactile bg-[#FAF7F2]/60">
                      <div className="w-14 text-center">
                        <div className="font-display font-bold text-lg">
                          {new Date(a.start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                        </div>
                        <div className="text-[10px] text-[#6B625B]">{a.duration_min}m</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{a.client_name} · {a.pet_name || "—"}</div>
                        <div className="text-xs text-[#6B625B] truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3"/> {a.address || "—"} · {a.service_name}
                        </div>
                      </div>
                      <div className={`pill border ${s.color}`}>{s.label}</div>
                      {NEXT[a.status] && (
                        <Button size="sm" onClick={() => advance(a)}
                          className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile"
                          data-testid={`advance-${a.id}`}>
                          <Icon className="w-3.5 h-3.5 mr-1"/> {NEXT_LABEL[a.status]}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
        </div>

        <div className="bg-[#2D4A3E] text-white rounded-3xl p-6 relative overflow-hidden grain">
          <div className="label-accent text-amber-300">Upcoming</div>
          <h3 className="font-display text-2xl font-bold mt-1 mb-4">This week</h3>
          {upcoming.length === 0 ? (
            <p className="text-sm text-white/70">No upcoming appointments.</p>
          ) : (
            <ul className="space-y-3">
              {upcoming.map(a => (
                <li key={a.id} className="flex items-center gap-3 border-t border-white/10 pt-3">
                  <div className="w-12 text-center">
                    <div className="font-display font-bold text-lg">
                      {new Date(a.start).toLocaleDateString("en-US", { day: "2-digit" })}
                    </div>
                    <div className="text-[10px] uppercase opacity-70">
                      {new Date(a.start).toLocaleDateString("en-US", { month: "short" })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-sm">{a.client_name}</div>
                    <div className="text-xs opacity-70 truncate">{a.service_name} · {a.pet_name}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint, fg, tid }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 relative overflow-hidden card-tactile" data-testid={tid}>
      <div className="w-10 h-10 rounded-xl grid place-content-center mb-3" style={{ background: tint, color: fg }}>
        <Icon className="w-5 h-5" strokeWidth={2.2}/>
      </div>
      <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
